import { useEffect, useRef, useState } from 'react';
import { formatSearchTime } from '../lib/format';
import {
  HISTORY_PAGE_SIZE,
  pageCount,
  pageOf,
  type SearchHistoryEntry,
} from '../lib/search-history';
import styles from './SearchHistory.module.css';

export interface SearchHistoryProps {
  /** Every recorded search, newest first. */
  entries: readonly SearchHistoryEntry[];
  onSelect: (entry: SearchHistoryEntry) => void;
  onRemove: (id: string) => void;
  onClear: () => void;
}

/**
 * Dropdown of every place searched for in this browser.
 *
 * A page holds ten rows, so the panel opens on the ten most recent searches
 * and the pager is what reaches the rest. Everything is kept — the pager is
 * the only reason the list stays readable once the history is long.
 */
export function SearchHistory({ entries, onSelect, onRemove, onClear }: SearchHistoryProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [page, setPage] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const onPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setIsOpen(false);
    };
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onEscape);
    };
  }, [isOpen]);

  // Clamped rather than stored: removing the last row of the last page would
  // otherwise strand the viewer on a page that no longer exists.
  const pages = pageCount(entries.length);
  const current = Math.min(page, pages - 1);

  const rows = pageOf(entries, current);
  const firstRow = current * HISTORY_PAGE_SIZE + 1;

  return (
    <div className={styles.wrap} ref={containerRef}>
      <button
        type="button"
        className={styles.trigger}
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        aria-haspopup="menu"
      >
        <HistoryIcon />
        <span className={styles.label}>History</span>
        {entries.length > 0 && <span className={styles.count}>{entries.length}</span>}
      </button>

      {isOpen && (
        <div className={styles.menu} role="menu">
          {entries.length === 0 ? (
            <p className={styles.empty}>
              Places you search for are listed here, in this browser only — nothing is sent to a
              server.
            </p>
          ) : (
            <>
              <ul className={styles.list}>
                {rows.map((entry) => (
                  <li key={entry.id} className={styles.item}>
                    <button
                      type="button"
                      role="menuitem"
                      className={styles.itemButton}
                      onClick={() => {
                        onSelect(entry);
                        setIsOpen(false);
                      }}
                    >
                      <span className={styles.itemLabel}>{entry.label}</span>
                      <span className={styles.itemTime}>{formatSearchTime(entry.searchedAt)}</span>
                    </button>
                    <button
                      type="button"
                      className={styles.remove}
                      onClick={() => onRemove(entry.id)}
                      aria-label={`Forget ${entry.label}`}
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>

              <div className={styles.footer}>
                <button
                  type="button"
                  className={styles.page}
                  disabled={current === 0}
                  onClick={() => setPage(current - 1)}
                  aria-label="Newer searches"
                >
                  ‹
                </button>

                <span className={styles.range} role="status">
                  {firstRow}–{firstRow + rows.length - 1} of {entries.length}
                </span>

                <button
                  type="button"
                  className={styles.page}
                  disabled={current >= pages - 1}
                  onClick={() => setPage(current + 1)}
                  aria-label="Older searches"
                >
                  ›
                </button>

                <button type="button" className={styles.clear} onClick={onClear}>
                  Clear all
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function HistoryIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      width="14"
      height="14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M2.4 8a5.6 5.6 0 1 0 1.7-4" />
      <path d="M2 2.2v2.6h2.6" />
      <path d="M8 5v3.2l2.2 1.3" />
    </svg>
  );
}
